import { Button, Form, Input, Spin } from "antd";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarClock,
  CircleCheck,
  FileQuestion,
  Lock,
  LogIn,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { useAppDispatch } from "../../app/app.store";
import { updateVerifiedInfo } from "../../app/features/auth/auth.slice";
import {
  useApplyJobMutation,
  useGetSingleJobQuery,
  useUpdateEmployeeProfileMutation,
} from "../../app/features/recruitment/recruitment.api";
import LoginModal from "../../components/LoginModal";
import useAuth from "../../hooks/useAuth";
import useDiscloser from "../../hooks/useDiscloser";
import WebLayout from "../../layouts/WebLayout";
import cn from "../../lib/cn";
import { getErrorMessage } from "../../lib/error";
import { toast } from "../../lib/toast";
import { formatDate } from "../../utils/date";
import { validateMobile } from "../../utils/validate-mobile";
import { PhotoField, ResumeField } from "./components/ApplyUploads";
import {
  EmptyState,
  MetaItem,
  OrgAvatar,
  panelClass,
} from "./components/portal";
import {
  formatExperience,
  formatSalary,
  getDeadline,
} from "./components/portal.utils";
import { PATHS } from "../../routes/paths";

const FormSection = ({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className={cn(panelClass, "p-5 sm:p-7")}>
    <div className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-theme-primary/10 text-xs font-semibold text-theme-primary">
        {step}
      </span>
      <div>
        <h2 className="text-[15px] font-semibold text-gray-900">{title}</h2>
        {description && (
          <p className="mt-0.5 text-[13px] text-gray-500">{description}</p>
        )}
      </div>
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

/** Centered message used for the signed-out, applied and success states. */
const StatusScreen = ({ children }: { children: ReactNode }) => (
  <WebLayout>
    <div className="mx-auto max-w-lg px-4 py-20 sm:py-28">{children}</div>
  </WebLayout>
);

const ApplyJob = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { verifiedInfo } = useAuth();
  const [form] = Form.useForm();
  const [config, handleOpen, handleClose] = useDiscloser<"LOGIN">();
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading: isJobLoading } = useGetSingleJobQuery(
    { userId: verifiedInfo?.id as string, id: jobId as string },
    { skip: !jobId },
  );
  const jobData = data?.data;

  const [applyJob, { isLoading: isApplying }] = useApplyJobMutation();
  const [updateProfile, { isLoading: isUpdating }] =
    useUpdateEmployeeProfileMutation();
  const isLoading = isApplying || isUpdating;

  useEffect(() => {
    // The form only mounts once the job has loaded.
    if (verifiedInfo && jobData) {
      form.setFieldsValue({
        name: verifiedInfo.name,
        email: verifiedInfo.email,
        phone: verifiedInfo.phone,
        photo: verifiedInfo.photo,
        cv_link: verifiedInfo.cv_link ? [verifiedInfo.cv_link] : [],
        nid: verifiedInfo.nid,
      });
    }
  }, [verifiedInfo, form, jobData]);

  const handleSubmit = async (values: any) => {
    try {
      if (!values?.cv_link?.[0] && !values?.photo) {
        toast("error", "Please upload your cv and profile photo.");
        return;
      }
      if (!values?.cv_link?.[0]) {
        toast("error", "Please upload your cv.");
        return;
      }
      if (!values?.photo) {
        toast("error", "Please upload your profile photo.");
        return;
      }
      const payload = {
        name: values.name,
        phone: values.phone,
        job_id: jobData?.id as string,
        photo: values.photo || "",
        email: verifiedInfo?.email || "",
        cv_link: values.cv_link?.[0] || "",
        expected_salary: String(values.expected_salary),
        nid: values.nid,
      };

      const res = await applyJob(payload).unwrap();

      if (res?.success) {
        // Also update the employee profile info
        if (verifiedInfo?.id) {
          await updateProfile({
            applicant_id: verifiedInfo.id,
            name: values.name,
            phone: values.phone,
            photo: values.photo || "",
            cv_link: values.cv_link?.[0] || "",
            nid: values.nid,
          }).unwrap();

          dispatch(
            updateVerifiedInfo({
              name: values.name,
              phone: values.phone,
              photo: values.photo || "",
              cv_link: values.cv_link?.[0] || "",
              nid: values.nid,
            }),
          );
        }

        toast(
          "success",
          getErrorMessage(res.message, "Application submitted successfully"),
        );
        form.resetFields();
        setSubmitted(true);
        window.scrollTo({ top: 0 });
      } else {
        toast(
          "error",
          getErrorMessage(res.message, "Failed to submit application"),
        );
      }
    } catch (error: any) {
      toast("error", getErrorMessage(error, "Something went wrong"));
    }
  };

  const jobPath = PATHS.jobPortal.job(jobId as string);

  /* ------------------------------ States ------------------------------ */

  if (isJobLoading) {
    return (
      <WebLayout>
        <div className="flex h-[70vh] items-center justify-center">
          <Spin size="large" />
        </div>
      </WebLayout>
    );
  }

  if (!jobData) {
    return (
      <StatusScreen>
        <EmptyState
          icon={FileQuestion}
          title="Job not found"
          description="This position may have been closed or removed."
          action={
            <Button
              onClick={() => navigate(PATHS.jobPortal.root)}
              className="btn-primary h-9 rounded-full px-5 font-medium"
            >
              Browse open jobs
            </Button>
          }
        />
      </StatusScreen>
    );
  }

  if (!verifiedInfo) {
    return (
      <StatusScreen>
        <EmptyState
          icon={LogIn}
          title="Log in to apply"
          description={`Log in to apply for ${jobData.job_title} and track your application from screening to offer.`}
          action={
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(jobPath)}
                className="h-9 rounded-full px-5 font-medium"
              >
                View job
              </Button>
              <Button
                onClick={() => handleOpen({ type: "LOGIN" })}
                className="btn-primary h-9 rounded-full px-5 font-medium"
              >
                Log in
              </Button>
            </div>
          }
        />
        <LoginModal
          data={config.data}
          type={config.type}
          handleClose={handleClose}
          open={config.open && config.type === "LOGIN"}
        />
      </StatusScreen>
    );
  }

  if (submitted || jobData.has_applied || jobData.applied_status) {
    return (
      <StatusScreen>
        <div className={cn(panelClass, "px-6 py-12 text-center sm:px-10")}>
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/50">
            <CircleCheck className="h-7 w-7" strokeWidth={1.75} />
          </span>
          <h1 className="mt-6 text-xl font-semibold text-gray-900">
            {submitted ? "Application submitted" : "You've already applied"}
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-gray-500">
            Your application for{" "}
            <span className="font-medium text-gray-900">
              {jobData.job_title}
            </span>{" "}
            at {jobData.org_name} is with the hiring team. You can follow its
            progress in Applied jobs.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-2 sm:flex-row">
            <Button
              onClick={() => navigate(PATHS.jobPortal.root)}
              className="h-10 rounded-full px-5 font-medium"
            >
              Browse more jobs
            </Button>
            <Button
              onClick={() => navigate(PATHS.jobPortal.applications)}
              className="btn-primary h-10 rounded-full px-5 font-medium"
            >
              View applied jobs
            </Button>
          </div>
        </div>
      </StatusScreen>
    );
  }

  /* ------------------------------- Form ------------------------------- */

  const deadline = getDeadline(jobData.job_application_deadline);
  const salary = formatSalary(
    jobData.salary_min_bdt,
    jobData.salary_max_bdt,
    jobData.is_show_salary_public,
  );
  const showSalaryRange =
    jobData.salary_min_bdt &&
    jobData.salary_max_bdt &&
    jobData.is_show_salary_public;

  return (
    <WebLayout>
      {/* ---------- Header ---------- */}
      <header className="relative overflow-hidden border-b border-gray-200 bg-white">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[620px] rounded-full bg-theme-primary/[0.12] blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <button
            onClick={() => navigate(jobPath)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to job details
          </button>
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Apply for {jobData.job_title}
          </h1>
          <p className="mt-2 text-[15px] text-gray-500">
            Takes about 2 minutes. Your details are pre-filled from your
            profile.
          </p>
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_320px] lg:gap-10 lg:px-8 lg:py-12">
        {/* Job summary — first on mobile, sidebar on desktop */}
        <aside className="lg:order-2">
          <div className={cn(panelClass, "p-5 lg:sticky lg:top-24")}>
            <div className="flex gap-3.5">
              <OrgAvatar name={jobData.org_name} />
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-semibold text-gray-900">
                  {jobData.job_title}
                </p>
                <p className="mt-0.5 truncate text-[13px] text-gray-500">
                  {jobData.org_name}
                  {jobData.department_name && ` · ${jobData.department_name}`}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-gray-100 pt-4 lg:flex-col">
              <MetaItem icon={BriefcaseBusiness}>
                {formatExperience(jobData.job_experience)}
              </MetaItem>
              <MetaItem icon={Wallet}>{salary}</MetaItem>
              <MetaItem icon={Users}>
                {jobData.vacancy_count} opening
                {jobData.vacancy_count === 1 ? "" : "s"}
              </MetaItem>
              {jobData.job_application_deadline && (
                <MetaItem icon={CalendarClock}>
                  Apply before {formatDate(jobData.job_application_deadline)}
                  {deadline?.urgent && (
                    <span className="ml-1 font-medium text-amber-600">
                      · {deadline.label}
                    </span>
                  )}
                </MetaItem>
              )}
            </div>
          </div>
        </aside>

        <Form
          form={form}
          size="large"
          layout="vertical"
          onFinish={handleSubmit}
          className="min-w-0 space-y-5 lg:order-1 [&_.ant-form-item-label>label]:!font-medium [&_.ant-form-item-label>label]:!text-gray-700 [&_.ant-form-item]:!mb-0"
        >
          <FormSection
            step={1}
            title="Personal details"
            description="Update anything that's changed — your profile is updated too."
          >
            <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
              <Form.Item
                label="Full name"
                name="name"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <Input placeholder="Enter your name" className="rounded-lg" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: "Please enter email" }]}
              >
                <Input
                  disabled
                  type="email"
                  placeholder="Enter your email"
                  suffix={<Lock className="h-3.5 w-3.5 text-gray-400" />}
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                label="Phone number"
                name="phone"
                rules={[
                  { required: true, message: "Please enter phone number" },
                  { validator: validateMobile },
                ]}
              >
                <Input
                  type="tel"
                  placeholder="01XXXXXXXXX"
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                label="NID number"
                name="nid"
                rules={[{ required: true, message: "Please enter NID" }]}
              >
                <Input
                  inputMode="numeric"
                  placeholder="19901234567890123"
                  className="rounded-lg"
                />
              </Form.Item>
            </div>
          </FormSection>

          <FormSection step={2} title="Expected salary">
            <Form.Item
              name="expected_salary"
              rules={[
                { required: true, message: "Please enter expected salary" },
              ]}
              extra={
                showSalaryRange ? (
                  <span className="mt-2 inline-block text-xs text-gray-500">
                    Offered range for this role:{" "}
                    <span className="font-medium text-gray-700">{salary}</span>
                  </span>
                ) : undefined
              }
            >
              <Input
                inputMode="numeric"
                prefix={<span className="mr-1 text-gray-400">৳</span>}
                placeholder="e.g. 50000"
                className="rounded-lg sm:max-w-xs"
              />
            </Form.Item>
          </FormSection>

          <FormSection
            step={3}
            title="Documents"
            description="Your photo and resume are shared with the hiring team."
          >
            {/* Registered so onFinish receives the uploaded values. */}
            <Form.Item name="photo" hidden />
            <Form.Item name="cv_link" hidden />
            <div className="grid gap-5 sm:grid-cols-2">
              <PhotoField form={form} />
              <ResumeField form={form} />
            </div>
          </FormSection>

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-xs text-gray-400 sm:text-left">
              By applying, you share these details with {jobData.org_name}.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(jobPath)}
                className="h-11 flex-1 rounded-full px-6 font-medium sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                loading={isLoading}
                className="btn-primary h-11 flex-1 rounded-full px-6 font-medium sm:flex-none"
              >
                Submit application
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </WebLayout>
  );
};

export default ApplyJob;
